import { Star, Mail, Phone, MapPin, Calendar, Award, GraduationCap, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { Candidate } from '@/data/mockData';

interface CandidateProfileProps {
  candidate: Candidate | null;
  open: boolean;
  onClose: () => void;
  onShortlist: (candidate: Candidate) => void;
  onScheduleInterview: (candidate: Candidate) => void;
  onAssignTask: (candidate: Candidate) => void;
}

export const CandidateProfile = ({
  candidate,
  open,
  onClose,
  onShortlist,
  onScheduleInterview,
  onAssignTask,
}: CandidateProfileProps) => {
  if (!candidate) return null;

  const initials = candidate.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="sr-only">
          <DialogTitle>Candidate Profile - {candidate.name}</DialogTitle>
        </DialogHeader>
        
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="text-2xl font-bold">Profile</h2>
            </div>

            {/* About Me */}
            <section>
              <h3 className="mb-3 text-lg font-semibold uppercase tracking-wide text-muted-foreground">
                About Me
              </h3>
              <p className="text-foreground leading-relaxed">{candidate.aboutMe}</p>
            </section>

            {/* Qualification */}
            <section>
              <h3 className="mb-3 text-lg font-semibold uppercase tracking-wide text-muted-foreground">
                Qualification
              </h3>
              <div className="grid grid-cols-3 gap-4 rounded-lg border border-border p-4">
                <div>
                  <p className="text-sm text-muted-foreground">Qualification</p>
                  <p className="font-medium">{candidate.education}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Institute</p>
                  <p className="font-medium">{candidate.institute}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Year of Passing</p>
                  <p className="font-medium">{candidate.graduationYear}</p>
                </div>
              </div>
            </section>

            {/* Certifications */}
            <section>
              <h3 className="mb-3 text-lg font-semibold uppercase tracking-wide text-muted-foreground">
                Certifications
              </h3>
              <div className="flex flex-wrap gap-2">
                {candidate.certifications.map((cert) => (
                  <Badge key={cert} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                    <Award className="h-3 w-3" />
                    {cert}
                  </Badge>
                ))}
              </div>
            </section>

            {/* Skills */}
            <section>
              <h3 className="mb-3 text-lg font-semibold uppercase tracking-wide text-muted-foreground">
                Skills
              </h3>
              <div className="flex flex-wrap gap-2">
                {candidate.skills.map((skill) => (
                  <Badge key={skill} className="bg-primary/10 text-foreground hover:bg-primary/20">
                    {skill}
                  </Badge>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section>
              <h3 className="mb-3 text-lg font-semibold uppercase tracking-wide text-muted-foreground">
                Contact Information
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span>{candidate.email}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{candidate.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{candidate.location}</span>
                </div>
              </div>
            </section>
          </div>

          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 rounded-xl border border-border bg-card p-6 text-center">
              {/* Avatar */}
              <div className="relative mx-auto mb-4">
                <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full border-4 border-accent bg-gradient-to-br from-primary to-accent text-3xl font-bold text-white">
                  {initials}
                </div>
                {candidate.isOnline && (
                  <span className="absolute bottom-2 right-1/2 translate-x-8 h-5 w-5 rounded-full border-3 border-card bg-success" />
                )}
              </div>

              <h3 className="text-xl font-bold">{candidate.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{candidate.title}</p>

              {/* Rating */}
              <div className="mt-2 flex items-center justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(candidate.rating)
                        ? 'fill-primary text-primary'
                        : 'text-muted-foreground'
                    }`}
                  />
                ))}
                <span className="ml-1 text-sm text-muted-foreground">
                  ({candidate.rating.toFixed(1)}/5)
                </span>
              </div>

              {/* Quick Info */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3">
                  <p className="text-lg font-bold">{candidate.dateOfBirth}</p>
                  <p className="text-xs text-muted-foreground">DOB</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-lg font-bold">{candidate.age}</p>
                  <p className="text-xs text-muted-foreground">Age</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-lg font-bold">{candidate.gender}</p>
                  <p className="text-xs text-muted-foreground">Gender</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-lg font-bold">{candidate.country}</p>
                  <p className="text-xs text-muted-foreground">Country</p>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-2">
                {candidate.status !== 'shortlisted' && candidate.status !== 'hired' && (
                  <Button className="w-full" onClick={() => onShortlist(candidate)}>
                    Shortlist Candidate
                  </Button>
                )}
                <Button variant="secondary" className="w-full" onClick={() => onScheduleInterview(candidate)}>
                  Schedule Interview
                </Button>
                <Button variant="outline" className="w-full" onClick={() => onAssignTask(candidate)}>
                  Assign Task
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
